import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';
import { getRemainingGenerations, getTotalAvailableCredits } from '../utilities/rate-limit';
import { useTranslation } from './useTranslation';

interface GenerateCardProxyParams {
  card: any;
  cardId: string;
  originalImage: string;
  customText: string;
  userEmail: string;
  userUid: string;
}

interface GenerateCardProxyResponse {
  cardImageUrl: string;
}

export const useGenerateCardProxy = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const abortControllerRef = useRef<AbortController | null>(null);

  const mutation = useMutation({
    mutationFn: async (params: GenerateCardProxyParams) => {
      // Cancel any existing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create a new AbortController for this request
      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      try {
        const response = await fetch('/api/generate-card-proxy', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Email': params.userEmail,
            'X-User-Uid': params.userUid,
          },
          body: JSON.stringify({
            card: params.card,
            cardId: params.cardId,
            originalImage: params.originalImage,
            customText: params.customText,
          }),
          signal, // Pass the abort signal to the fetch request
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || t('customizeCardModal.error.failed'));
        }

        return response.json() as Promise<GenerateCardProxyResponse>;
      } catch (error) {
        // Check if the error is due to an aborted request
        if (error instanceof DOMException && error.name === 'AbortError') {
          throw new Error('cancelled');
        }
        throw error;
      }
    },
    onSuccess: (_, params) => {
      // Invalidate relevant queries to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ['remainingGenerations'] });
      queryClient.invalidateQueries({ queryKey: ['userCredits', params.userUid] });
    },
  });

  return {
    ...mutation,
    cancel: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    },
  };
};

export const useRemainingGenerations = (stripeCustomerId: string | undefined) => {
  return useQuery({
    queryKey: ['remainingGenerations', stripeCustomerId],
    queryFn: () => getRemainingGenerations(stripeCustomerId!),
    enabled: !!stripeCustomerId,
  });
};

export const useTotalAvailableCredits = (userId: string | undefined, stripeCustomerId: string | undefined) => {
  return useQuery({
    queryKey: ['totalAvailableCredits', userId],
    queryFn: () => getTotalAvailableCredits(userId!, stripeCustomerId!),
    enabled: !!userId && !!stripeCustomerId,
  });
};
